import { supabase } from '@/lib/supabase';
import type { AppMember, AppRecord } from '@/lib/app-records';

/**
 * F5c: the human review of an app's objects with its grid (here MEDDIC),
 * next to the AI's grid of the run, and the team view built from the reviews.
 */
export interface ReviewCriterion {
  key: string;
  label: string;
  /** Words that find the matching criterion in the AI's grid. */
  aliases?: string[];
}

export interface AppReview {
  id: string;
  recordId: string;
  reviewerUserId: string;
  scores: Record<string, number>;
  comment: string | null;
  updatedAt: string;
}

export interface AiSegment {
  label: string;
  value: number;
  max?: number;
}

export const SCORE_LABELS = ['Absent', 'Évoqué', 'Qualifié', 'Validé'] as const;
export const SCORE_MAX = 3;

/** The grid decided with the FDE for this example (decision of 05/10/2026). */
export const MEDDIC_GRID: ReviewCriterion[] = [
  { key: 'metrics', label: 'Metrics — impact chiffré', aliases: ['metric', 'impact'] },
  { key: 'economic_buyer', label: 'Décideur économique', aliases: ['economic', 'decideur', 'budget'] },
  { key: 'decision_criteria', label: 'Critères de décision', aliases: ['criteria', 'criteres'] },
  { key: 'decision_process', label: 'Processus de décision', aliases: ['process'] },
  { key: 'identify_pain', label: 'Douleur identifiée', aliases: ['pain', 'douleur'] },
  { key: 'champion', label: 'Champion', aliases: ['champion'] },
];

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** The AI's segment for a criterion: same label, or a label containing one of its aliases. */
export const matchAiSegment = (criterion: ReviewCriterion, segments: AiSegment[]): AiSegment | undefined => {
  const label = normalize(criterion.label);
  return segments.find((segment) => normalize(segment.label) === label)
    ?? segments.find((segment) => (criterion.aliases ?? []).some((alias) => normalize(segment.label).includes(normalize(alias))));
};

export const loadAppReviews = async (appId: string): Promise<AppReview[]> => {
  const { data, error } = await supabase.from('app_record_reviews')
    .select('id, record_id, reviewer_user_id, scores, comment, updated_at').eq('tool_app_id', appId);
  if (error) throw new Error('Impossible de charger les revues.');
  return (data ?? []).map((row) => ({
    id: row.id, recordId: row.record_id, reviewerUserId: row.reviewer_user_id,
    scores: row.scores as Record<string, number>, comment: row.comment, updatedAt: row.updated_at,
  }));
};

export const saveAppReview = async (recordId: string, scores: Record<string, number>, comment: string) => {
  const { error } = await supabase.rpc('save_app_record_review_f5c', { p_record_id: recordId, p_scores: scores, p_comment: comment });
  if (error) throw new Error(error.message === 'app_review_scores_invalid' ? 'Notez chaque critère avant d’enregistrer.' : 'La revue n’a pas pu être enregistrée.');
};

export const setAppReviewGrid = async (appId: string, grid: ReviewCriterion[] | null) => {
  const { error } = await supabase.rpc('set_app_review_grid_f5c', { p_tool_app_id: appId, p_grid: grid });
  if (error) throw new Error(error.message === 'app_review_grid_forbidden' ? 'Seuls le créateur de l’application et les managers règlent la grille.' : 'La grille n’a pas pu être enregistrée.');
};

export const averageScore = (review: AppReview, grid: ReviewCriterion[]): number =>
  grid.reduce((sum, criterion) => sum + (review.scores[criterion.key] ?? 0), 0) / Math.max(1, grid.length);

export interface TeamRow {
  ownerUserId: string;
  name: string;
  objects: number;
  reviewed: number;
  /** Average human score per criterion, null when nothing is reviewed. */
  averages: Record<string, number | null>;
  overall: number | null;
  weakest: ReviewCriterion | null;
}

/** Per person: their objects, how many are reviewed, and the average of the latest review of each, per criterion. */
export const teamSummary = (records: AppRecord[], reviews: AppReview[], grid: ReviewCriterion[], members: AppMember[]): TeamRow[] => {
  const latestByRecord = new Map<string, AppReview>();
  for (const review of reviews) {
    const current = latestByRecord.get(review.recordId);
    if (!current || current.updatedAt < review.updatedAt) latestByRecord.set(review.recordId, review);
  }
  const owners = [...new Set(records.filter((record) => record.ownerUserId && !record.archivedAt).map((record) => record.ownerUserId!))];
  return owners.map((ownerUserId) => {
    const mine = records.filter((record) => record.ownerUserId === ownerUserId && !record.archivedAt);
    const reviewed = mine.map((record) => latestByRecord.get(record.id)).filter((review): review is AppReview => Boolean(review));
    const averages = Object.fromEntries(grid.map((criterion) => [criterion.key,
      reviewed.length ? reviewed.reduce((sum, review) => sum + (review.scores[criterion.key] ?? 0), 0) / reviewed.length : null]));
    const scored = grid.filter((criterion) => averages[criterion.key] !== null);
    const weakest = scored.length ? scored.reduce((low, criterion) => (averages[criterion.key]! < averages[low.key]! ? criterion : low)) : null;
    return {
      ownerUserId,
      name: members.find((member) => member.userId === ownerUserId)?.name ?? 'Membre',
      objects: mine.length, reviewed: reviewed.length, averages,
      overall: scored.length ? scored.reduce((sum, criterion) => sum + averages[criterion.key]!, 0) / scored.length : null,
      weakest,
    };
  }).sort((left, right) => left.name.localeCompare(right.name, 'fr'));
};
